<template>
    <SettingsCard title="Настройки должностей">
        <div v-if="loading" class="ds-settings-position-state">Загрузка должностей и графиков…</div>
        <div v-else-if="loadError" class="ds-settings-position-state-error" role="alert">
            <span>Не удалось загрузить должности или графики.</span>
            <btn @click="load">Повторить</btn>
        </div>
        <template v-else>
            <div v-if="!positions.length" class="ds-settings-position-state">Должности не найдены.</div>
            <template v-else>
                <div class="ds-settings-positions-header" aria-hidden="true">
                    <span>Полное название</span>
                    <span>Короткое название</span>
                    <span>График</span>
                </div>
                <div v-for="position in positions" :key="position.id" class="ds-settings-position-row">
                    <div class="ds-settings-position-name">{{ position.name }}</div>
                    <base-input
                        v-model="position.short_name"
                        max-length="3"
                        class="ds-settings-position-input"
                        :disabled="!canEditPositions || saving"
                    />
                    <dropdown
                        :model-value="getSchedule(position.shift_schedule)"
                        :options="shiftSchedules"
                        class="ds-settings-position-schedule"
                        :disabled="!canEditPositions || saving"
                        @update:model-value="position.shift_schedule = $event.id"
                    >
                        {{ getSchedule(position.shift_schedule)?.name || 'Выберите график' }}
                    </dropdown>
                </div>
                <div class="ds-settings-actions">
                    <btn @click="save" :disabled="isDisabled" :class="{ 'opacity-50': isDisabled }">Сохранить</btn>
                </div>
            </template>
        </template>
        <div v-if="saveError" class="ds-settings-error" role="alert">
            Не удалось сохранить изменения должностей. <button type="button" @click="save">Повторить</button>
        </div>
    </SettingsCard>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { editPosition } from '@/api/schedule'
import { useAuthStore } from '@/stores/authStore'
import { useScheduleStore } from '@/stores/scheduleStore'
import type { Option, Position } from '@/types'
import SettingsCard from '@/ui/SettingsCard.vue'

type PositionChanges = Partial<Pick<Position, 'short_name' | 'shift_schedule'>>
type OriginalPosition = Required<PositionChanges>

const positions = ref<Position[]>([])
const originalPositions = ref<Record<string, OriginalPosition>>({})
const shiftSchedules = ref<Option[]>([])
const loading = ref(true)
const saving = ref(false)
const loadError = ref(false)
const saveError = ref(false)
const authStore = useAuthStore()
const scheduleStore = useScheduleStore()

const canEditPositions = computed(() => Boolean(authStore.permissions?.can_edit_position))
const isShortNameChanged = (current: string | null, original: string | null) => (current ?? '') !== (original ?? '')
const changedPositions = computed(() => positions.value.filter((position) => {
    const original = originalPositions.value[position.id]
    return original && (
        isShortNameChanged(position.short_name, original.short_name)
        || original.shift_schedule !== position.shift_schedule
    )
}))
const isDisabled = computed(() => !canEditPositions.value || loading.value || saving.value || !changedPositions.value.length)

const getSchedule = (id: string | null) => shiftSchedules.value.find((schedule) => schedule.id === id)

const setOriginalPosition = (position: Position) => {
    originalPositions.value[position.id] = {
        short_name: position.short_name,
        shift_schedule: position.shift_schedule,
    }
}

const load = async () => {
    loading.value = true
    loadError.value = false

    try {
        const [loadedPositions, loadedSchedules] = await Promise.all([
            scheduleStore.fetchPositions(),
            scheduleStore.fetchShiftSchedules(),
        ])
        if (!Array.isArray(loadedPositions) || !Array.isArray(loadedSchedules)) {
            console.error('Получены некорректные данные должностей или графиков')
            loadError.value = true
            return
        }
        positions.value = loadedPositions.map((position: Position) => ({ ...position }))
        originalPositions.value = {}
        positions.value.forEach(setOriginalPosition)
        shiftSchedules.value = loadedSchedules
    } catch (error) {
        console.error('Не удалось загрузить должности или графики', error)
        loadError.value = true
    } finally {
        loading.value = false
    }
}

const getChanges = (position: Position): PositionChanges => {
    const original = originalPositions.value[position.id]
    const changes: PositionChanges = {}

    if (isShortNameChanged(position.short_name, original.short_name)) changes.short_name = position.short_name
    if (position.shift_schedule !== original.shift_schedule) changes.shift_schedule = position.shift_schedule

    return changes
}

const save = async () => {
    saving.value = true
    saveError.value = false

    const results = await Promise.allSettled(changedPositions.value.map(async (position) => {
        const response = await editPosition(position.id, getChanges(position))
        return { id: position.id, position: response.data.body as Position }
    }))

    results.forEach((result) => {
        if (result.status !== 'fulfilled') return

        const index = positions.value.findIndex((position) => position.id === result.value.id)
        if (index === -1) return

        positions.value[index] = { ...result.value.position }
        setOriginalPosition(result.value.position)

        const storeIndex = scheduleStore.positions.findIndex((position) => position.id === result.value.id)
        if (storeIndex !== -1) scheduleStore.positions[storeIndex] = { ...result.value.position }
    })

    saveError.value = results.some((result) => result.status === 'rejected')
    saving.value = false
}

onMounted(load)
</script>
